/**
 * @wr/contracts — the single source of truth for REST payload shapes.
 *
 * Zod schemas here drive: Fastify request validation in `apps/api`, client
 * types in `apps/web`, and (Phase 2) generated admin forms. Nothing in this
 * package may import `@wr/db` — contracts are what the client is allowed to
 * see; Drizzle row types are not (architecture §9).
 *
 * `engines.ts` is the one member that is not a payload shape: A54 puts
 * `ENGINE_KEYS` here because this package is zod-only and every tier may already
 * depend on it, which is what lets one list reach the api, the game tier and the
 * client without dragging Colyseus into the api's graph.
 */
export * from "./engines.js";
export * from "./error.js";
/**
 * `legal.ts` is the second member that is not a payload shape, for the same
 * reason `engines.ts` is the first: A94's ToS version has to reach the api's two
 * write sites and `apps/web`'s drift check, and this is the zod-only package
 * that every tier already depends on.
 */
export * from "./legal.js";
export * from "./health.js";
/**
 * `sides.ts` is the third member that is not a payload shape, and it is here for
 * the same reason `engines.ts` and `legal.ts` are: S11 spec 3 requires the frozen
 * SIDES table to live in **the lowest package both `apps/game` and `apps/web`
 * already depend on**, and this zero-runtime-dependency package is it. See its
 * header for the manifest measurement that settles the choice.
 */
export * from "./sides.js";
export * from "./seat-reservation.js";
export * from "./internal-seats.js";
export * from "./players.js";
export * from "./profile.js";
export * from "./stats.js";
export * from "./transactions.js";
export * from "./lobby.js";
export * from "./room-summary.js";
export * from "./rooms.js";
/**
 * `room-refusal.ts` is the fourth member that is not a payload shape, and Dex
 * Add. 10 §1 settles the residence on the same argument the other three use
 * plus one that is measured: `packages/contracts/**` is the ONLY path in api's,
 * web's AND game's `watchPatterns`, and a refusal token is protocol rather than
 * engine code or copy.
 */
export * from "./room-refusal.js";
export * from "./balance.js";
export * from "./rewards.js";
/**
 * `shop.ts` is A121 Item 3's wire freeze: the closed refusal vocabulary and the
 * purchase `nonce`, declared a full wave before the routes that serve them, so
 * the pricing wave is a body swap rather than a wire change on a money path.
 */
export * from "./shop.js";
/** Every REST route is served under this prefix. */
export const API_V1 = "/api/v1";
//# sourceMappingURL=index.js.map